// src/components/AllLessonsFrontend.js

import React, { useState, useEffect } from 'react';
import { get, post, put, del } from '../../utils/api'; // Import put function
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { confirmAlert } from 'react-confirm-alert';
import { Button } from '@/components/shadcn/button';
import 'react-confirm-alert/src/react-confirm-alert.css';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/shadcn/accordion';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/shadcn/table';
import moment from 'moment';

function AllLessonsFrontend() {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTopic, setCurrentTopic] = useState(null);
  const [isAllFinished, setIsAllFinished] = useState(false);
  const navigate = useNavigate();
  const { logout, loggedInChild, user } = useAuth();

  useEffect(() => {
    fetchAllTopics();
    if (loggedInChild) {
      fetchCurrentTopic();
    }
  }, [loggedInChild]);

  const fetchAllTopics = async () => {
    try {
      const response = await get('/topics/visible');
      setTopics(response);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch topics');
      setLoading(false);
    }
  };

  const fetchCurrentTopic = async () => {
    try {
      const response = await get(`/children/${loggedInChild._id}/current-home-topic`, true);
      setCurrentTopic(response.homeCurrentTopicId);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch current topic');
      setLoading(false);
    }
  };

  const handleContinue = async () => {
    try {
      const response = await get(`/childTopics/${loggedInChild._id}/${currentTopic._id}?track=home`, true);
      const { currentLesson, learningStage } = response;

      if (learningStage === 'lesson') {
        if (!currentLesson) {
          confirmAlert({
            title: 'Lesson Not Found',
            message: 'The lesson no longer exists, please select another.',
            buttons: [
              {
                label: 'OK',
                onClick: async () => {
                  try {
                    // Delete the ChildTopic entry
                    await del(`/childTopics/${loggedInChild._id}/${currentTopic._id}?track=home`, true);

                    // Clear the homeCurrentTopicId from the child document
                    await put(`/children/${loggedInChild._id}/current-home-topic`, { topicId: null }, true);

                    fetchCurrentTopic();
                  } catch (error) {
                    console.error('Error clearing current topic or deleting ChildTopic entry:', error);
                  }
                }
              }
            ]
          });
          return;
        }

        const lessonExists = await get(`/lessons/${currentLesson._id}`);
        if (!lessonExists) {
          confirmAlert({
            title: 'Lesson Not Found',
            message: 'The lesson no longer exists, please select another.',
            buttons: [
              {
                label: 'OK',
                onClick: async () => {
                  try {
                    // Delete the ChildTopic entry
                    await del(`/childTopics/${loggedInChild._id}/${currentTopic._id}?track=home`, true);

                    // Clear the homeCurrentTopicId from the child document
                    await put(`/children/${loggedInChild._id}/current-home-topic`, { topicId: null }, true);

                    fetchCurrentTopic();
                  } catch (error) {
                    console.error('Error clearing current topic or deleting ChildTopic entry:', error);
                  }
                }
              }
            ]
          });
          return;
        }
        navigate(`/lesson/${currentLesson._id}`);
      } else if (learningStage === 'quiz') {
        const quizSubmissions = await get(`/quiz/quiz-submissions/search?childId=${loggedInChild._id}&topicId=${currentTopic._id}&lessonId=${currentLesson._id}&QuizStatus=Pending&isHomework=false&track=home&isForcedRed=false`, true);

        if (quizSubmissions && quizSubmissions.length > 0) {
          const quizSubmissionId = quizSubmissions[0]._id;
          navigate(`/quiz/${currentTopic._id}/${currentLesson._id}`, { state: { quizSubmissionId } });
        } else {
          console.log('No pending quiz submission found, creating a new one');
          try {
            const response = await post('/quiz/quiz-submissions', {
              lessonId: currentLesson._id,
              userId: user._id,
              childId: loggedInChild._id,
              topicId: currentTopic._id,
              QuizStatus: 'Pending',
              isCoreSkill: false,
              track: 'home',
              isForcedRed: false,
            }, true);

            navigate(`/quiz/${currentTopic._id}/${currentLesson._id}`, { state: { quizSubmissionId: response._id } });
          } catch (error) {
            console.error('Error creating new quiz submission:', error);
            confirmAlert({
              title: 'Error',
              message: 'Failed to create a quiz. Please try again or contact support.',
              buttons: [
                {
                  label: 'OK',
                  onClick: () => { }
                }
              ]
            });
          }
        }
      } else {
        console.error('Invalid learning stage:', learningStage);
        // Handle invalid learning stage
      }
    } catch (err) {
      confirmAlert({
        title: 'Error',
        message: 'Failed to continue. Please try again.',
        buttons: [
          {
            label: 'OK',
            onClick: () => { },
          },
        ],
      });
    }
  };


  const handleLogout = () => {
    logout();
    navigate('/login'); // Redirect to login page after logout
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p>{error}</p>;

  return (
    <div className="container mx-auto p-4 min-h-screen flex flex-col">
      {!currentTopic && (
        <div className="mb-4 pb-2 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-foreground">All Topics</h1>
          </div>
        </div>
      )}

      {currentTopic && currentTopic.topicId ? (
        <div className="flex-grow flex items-center justify-center">
          {isAllFinished ? (
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-4">All finished for now!</h2>
              <p>We'll be in touch with next steps!</p>
            </div>
          ) : (
            <Button
              onClick={handleContinue}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Continue from last time
            </Button>
          )}
        </div>
      ) : (
        <Accordion type="single" collapsible className="w-full">
          {topics.filter(topic => topic.topic !== 'Core Skills').map((topic) => (
            <AccordionItem key={topic._id} value={topic._id}>
              <AccordionTrigger className="text-xl font-bold">
                {topic.topic}
              </AccordionTrigger>
              <AccordionContent>
                <LessonsList topic={topic} lessons={topic.lessons}/>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
}

export default AllLessonsFrontend;


export function LessonsList({ topic, lessons, canAccess = true, lessonAccessState = 'pre_quiz_pending', releaseDate = null, completionDate = null, releaseHours = 48, isCoreSkill = false }) {
  const { loggedInChild, user } = useAuth();
  const navigate = useNavigate();

  const requireLoggedInChild = () => {
    if (!loggedInChild) {
      confirmAlert({
        title: 'Error',
        message: 'Please login as a child before selecting a lesson to learn',
        buttons: [{ label: 'OK', onClick: () => {} }],
      });
      return false;
    }
    return true;
  };

  const handleLessonClick = async (topicId, lessonId) => {
    if (!requireLoggedInChild()) return;
    try {
      await post('/childTopics/start-learning', { childId: loggedInChild._id, topicId, lessonId, track: 'home' }, true);
      navigate(`/core-skills/learn/${lessonId}?isCoreSkill=${isCoreSkill}&coreSkillTopicId=${topic._id}`);
    } catch (err) {
      confirmAlert({
        title: 'Error',
        message: 'Failed to start learning. Please try again.',
        buttons: [{ label: 'OK', onClick: () => {} }],
      });
    }
  };

  const handlePreQuizClick = async (topicId, lessonId) => {
    if (!requireLoggedInChild()) return;
    try {
      const submission = await post('/quiz/quiz-submissions', {
        lessonId,
        userId: user._id,
        childId: loggedInChild._id,
        topicId,
        QuizStatus: 'Pending',
        isCoreSkill: true,
        isPreQuiz: true,
        createdAt: new Date(),
      }, true);
      navigate(`/core-skills/quiz/${topicId}/${lessonId}`, { state: { quizSubmissionId: submission._id } });
    } catch (err) {
      confirmAlert({
        title: 'Error',
        message: 'Failed to start pre-quiz. Please try again.',
        buttons: [{ label: 'OK', onClick: () => {} }],
      });
    }
  };

  if (!lessons || lessons?.length === 0) {
    return <p>No lessons available for this topic.</p>;
  }

  return (
    <>
      {isCoreSkill && lessonAccessState === 'complete_full' && (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4" role="alert">
          <p className="font-bold">Great work!</p>
          <p className="text-sm text-gray-900">
            {`Great work completing the Core Skills lesson! Your next lesson will be available by ${moment(completionDate || releaseDate).add(releaseHours, 'hours').format('MMMM Do YYYY, h:mm:ss a')}`}
          </p>
        </div>
      )}
      {isCoreSkill && lessonAccessState === 'streak_cap_reached' && (
        <div className="bg-orange-100 border-l-4 border-orange-500 text-orange-700 p-4 mb-4" role="alert">
          <p className="font-bold">Daily limit reached!</p>
          <p className="text-sm text-gray-900">
            {`You've completed the maximum number of pre-quizzes. Your next Core Skills lesson will be available by ${moment(completionDate || releaseDate).add(releaseHours, 'hours').format('MMMM Do YYYY, h:mm:ss a')}`}
          </p>
        </div>
      )}
      {isCoreSkill && lessonAccessState === 'complete_pre_quiz' && (
        <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4" role="alert">
          <p className="font-bold">Excellent!</p>
          <p className="text-sm">The next Core Skills lesson will be released in 1 minute.</p>
        </div>
      )}
      {isCoreSkill && lessonAccessState === 'lesson_accessible' && (
        <div className="bg-blue-100 border-l-4 border-blue-500 text-blue-700 p-4 mb-4" role="alert">
          <p className="text-sm">You didn't pass the pre-quiz, but you can still complete the full lesson below.</p>
        </div>
      )}
      <Table>
        <TableHeader className="max-md:hidden">
          <TableRow>
            <TableHead className="text-lg font-bold">Lesson</TableHead>
            <TableHead className="text-lg font-bold">Year Group</TableHead>
            <TableHead className="text-lg font-bold">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {lessons
            .filter(lesson => isCoreSkill && topic.topic !== 'Core Skills' ? lesson.lessonId.lessonVisible : true)
            .map((lesson) => {
              lesson = !isCoreSkill ? lesson.lessonId : lesson;
              if (!lesson) return null;
              return (
                <TableRow key={lesson._id} className="max-md:flex max-md:flex-col max-md:py-2">
                  <TableCell className="text-base font-semibold max-md:py-1">{lesson.title}</TableCell>
                  <TableCell className="text-base font-semibold max-md:py-1 max-md:text-sm max-md:font-normal max-md:text-gray-600">
                    {lesson.yearGroupLevels?.length ? lesson.yearGroupLevels.join(', ') : (lesson.yearGroupLevel || 'N/A')}
                  </TableCell>
                  <TableCell className="text-center flex gap-2 justify-center max-md:flex-col max-md:items-stretch max-md:py-2">
                    {isCoreSkill && lessonAccessState === 'pre_quiz_pending' && (
                      <Button
                        onClick={() => handlePreQuizClick(topic._id, lesson._id)}
                        className="max-md:h-11 bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                      >
                        Take Pre-Quiz
                      </Button>
                    )}
                    <Button
                      disabled={isCoreSkill && lessonAccessState !== 'lesson_accessible'}
                      onClick={() => handleLessonClick(topic._id, lesson._id)}
                      className="max-md:h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Start Learning
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
        </TableBody>
      </Table>
    </>
  );
}